import api from "../../services/api";
import { AssetsResponse, Asset, CreateAssetDto } from "../../types";


interface FetchAssetsParams {
  estateId: string;
  page?: number;
  limit?: number;
}

export class AssetService {
  /**
   * Fetch paginated assets along with total values and comparison metrics.
   * @param estateId - UUID of the estate.
   * @param page - Current page number.
   * @param limit - Number of assets per page.
   * @returns A promise that resolves to AssetsResponse.
   */
  static async fetchAssets({ estateId, page = 1, limit = 10 }: FetchAssetsParams): Promise<AssetsResponse> {
    try {
      const response = await api.get<AssetsResponse>(`/assets/${estateId}`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error: any) {
      // Handle errors appropriately
      console.error('Error fetching assets:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch assets.');
    }
  }

  /**
   * Fetch a single asset by ID.
   * @param estateId - UUID of the estate.
   * @param assetId - ID of the asset.
   * @returns A promise that resolves to an Asset.
   */
  static async fetchAssetById(estateId: string, assetId: string): Promise<Asset> {
    try {
      const response = await api.get<Asset>(`/assets/${estateId}/${assetId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching asset:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch asset.');
    }
  }

  /**
   * Create a new asset.
   * @param estateId - UUID of the estate.
   * @param assetData - Data for the new asset.
   * @returns A promise that resolves to the created Asset.
   */
  static async createAsset(estateId: string, assetData: CreateAssetDto): Promise<Asset> {

    try {
      const response = await api.post<Asset>(`/assets/${estateId}`, assetData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating asset:', error);
      throw error;
    }
  }

  /**
   * Update an existing asset.
   * @param estateId - UUID of the estate.
   * @param assetId - ID of the asset to update.
   * @param updateData - Updated data for the asset.
   * @returns A promise that resolves to the updated Asset.
   */
  static async updateAsset(estateId: string, assetId: string, updateData: Partial<Asset>): Promise<Asset> {
    try {
      const response = await api.patch<Asset>(`/assets/${estateId}/${assetId}`, updateData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating asset:', error);
      throw new Error(error.response?.data?.message || 'Failed to update asset.');
    }
  }

  /**
   * Delete an asset.
   * @param estateId - UUID of the estate.
   * @param assetId - ID of the asset to delete.
   * @returns A promise that resolves when the asset is deleted.
   */
  static async deleteAsset(estateId: string, assetId: string): Promise<void> {
    try {
      await api.delete(`/assets/${estateId}/${assetId}`);
    } catch (error: any) {
      console.error('Error deleting asset:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete asset.');
    }
  }

  /**
   * Search assets by name or description within a specific estate.
   * @param estateId - UUID of the estate.
   * @param query - Search term.
   * @returns A promise that resolves to an array of matching assets.
   */
  static async searchAssets(estateId: string, query: string): Promise<Asset[]> {
    try {
      const response = await api.get<Asset[]>(`/assets/${estateId}/search?q=${query}`);
      return response.data;
    } catch (error: any) {
      console.error('Error searching assets:', error);
      throw new Error(error.response?.data?.message || 'Failed to search assets.');
    }
  }

  /**
  * Downloads assets as a CSV file.
  * @param estateId - UUID of the estate.
  * @param from - Optional start date for filtering.
  * @param to - Optional end date for filtering.
  * @returns A promise that resolves to a string containing the CSV data.
  */
  static async downloadAssetsCsv(estateId: string, from?: string | null, to?: string | null): Promise<string> {
    try {
      let url = `/assets/${estateId}/export/csv`;
      const params = new URLSearchParams();
      if (from) {
        params.append('from', from);
      }
      if (to) {
        params.append('to', to);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await api.get<string>(url, {
        responseType: 'text', // Important: Ensures the response is treated as text
      });
      return response.data;
    } catch (error: any) {
      console.error('Error downloading assets CSV:', error);
      throw new Error(error.response?.data?.message || 'Failed to download assets CSV.');
    }
  }

}



export const getRecentAssets = async (
  estateId: string,
  count: number = 5
): Promise<Asset[]> => {
  const response = await api.get(`/assets/${estateId}/recent/${count}`);
  return response.data;
};

